// Renderer - canvas drawing utilities

import { pixelTextSprite } from './pixel-text.js';
import { PIXEL_SCALE } from './pixel-art.js';

// --- Sprite helpers (shared by every entity that draws pixel art) ---
//
// All three caches exist for the same reason: at 100+ guards on screen the
// per-frame cost of rebuilding sprite data dominated the frame. They are
// keyed by *object identity*, so callers must pass stable references —
// module-level sprite constants and palettes, never freshly-built literals.

const _flipCache = new Map();

// Mirror a pixel-art sprite horizontally. Previously every entity redid
// `sprite.map(row => split/reverse/join)` on every frame it faced left,
// allocating a fresh array of strings per entity per frame.
export function flipSprite(sprite) {
  let flipped = _flipCache.get(sprite);
  if (!flipped) {
    flipped = sprite.map(row => row.split('').reverse().join(''));
    _flipCache.set(sprite, flipped);
  }
  return flipped;
}

const _whiteCache = new Map();

// All-white variant of a palette, used for the damage flash. Cached so the
// flash frames reuse one object (and therefore one rasterised sprite)
// instead of minting a new palette literal every frame.
export function whitePalette(palette) {
  let white = _whiteCache.get(palette);
  if (!white) {
    white = {};
    for (const key in palette) white[key] = '#fff';
    _whiteCache.set(palette, white);
  }
  return white;
}

// Rasterised (sprite × palette × scale) → offscreen canvas.
// Bounded defensively: a caller that leaks fresh palette objects would
// otherwise grow this without limit. Sized for the guards' per-direction
// weapon overlays (caravan.js) on top of the body sprites — overflowing
// clears everything and re-rasterises, so the bound must not be hit in play.
const _rasterCache = new Map();
const RASTER_CACHE_MAX = 1024;
let _rasterCacheSize = 0;

function rasterizeSprite(pixels, palette, scale) {
  if (typeof document === 'undefined') return null;
  const rows = pixels.length;
  if (rows === 0) return null;
  const cols = pixels[0].length;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, cols * scale);
  canvas.height = Math.max(1, rows * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  for (let r = 0; r < rows; r++) {
    const row = pixels[r];
    for (let c = 0; c < cols; c++) {
      const ch = row[c];
      if (ch === undefined || ch === ' ' || ch === '.') continue;
      const color = palette[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(c * scale, r * scale, scale, scale);
    }
  }
  return canvas;
}

function cachedSprite(pixels, palette, scale) {
  let byPalette = _rasterCache.get(pixels);
  if (!byPalette) {
    byPalette = new Map();
    _rasterCache.set(pixels, byPalette);
  }
  let byScale = byPalette.get(palette);
  if (!byScale) {
    byScale = new Map();
    byPalette.set(palette, byScale);
  }
  if (byScale.has(scale)) return byScale.get(scale);
  if (_rasterCacheSize >= RASTER_CACHE_MAX) {
    _rasterCache.clear();
    _rasterCacheSize = 0;
    return rasterizeSprite(pixels, palette, scale);
  }
  const canvas = rasterizeSprite(pixels, palette, scale);
  byScale.set(scale, canvas);
  _rasterCacheSize++;
  return canvas;
}

export class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.width = 0;
    this.height = 0;
    this.resize();
  }

  resize() {
    const dpr = 1; // keep 1:1 for pixel art crispness
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx.imageSmoothingEnabled = false;
  }

  clear(color = '#1a1a2e') {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  rect(x, y, w, h, color) {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  strokeRect(x, y, w, h, color, lineWidth = 1) {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.strokeRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  circle(x, y, r, color) {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.arc(Math.round(x), Math.round(y), r, 0, Math.PI * 2);
    this.ctx.fill();
  }

  triangle(x1, y1, x2, y2, x3, y3, color) {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.lineTo(x3, y3);
    this.ctx.closePath();
    this.ctx.fill();
  }

  // Inline gold-coin icon: outer disk + brighter inner disk + dark center.
  // Matches the in-world Loot rendering so HUD/shop visually agree.
  coinIcon(x, y, size = 14) {
    const r = size / 2;
    this.circle(x, y, r, '#ffd700');
    this.circle(x - r * 0.2, y - r * 0.2, Math.max(1, r * 0.55), '#ffe44d');
    this.circle(x, y, Math.max(1, r * 0.18), '#c9a100');
  }

  strokeCircle(x, y, r, color, lineWidth = 1) {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.beginPath();
    this.ctx.arc(Math.round(x), Math.round(y), r, 0, Math.PI * 2);
    this.ctx.stroke();
  }

  strokeArc(x, y, r, startAngle, endAngle, color, lineWidth = 1) {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.lineCap = 'round';
    this.ctx.beginPath();
    this.ctx.arc(x, y, r, startAngle, endAngle);
    this.ctx.stroke();
    this.ctx.lineCap = 'butt';
  }

  line(x1, y1, x2, y2, color, lineWidth = 1) {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(Math.round(x1), Math.round(y1));
    this.ctx.lineTo(Math.round(x2), Math.round(y2));
    this.ctx.stroke();
  }

  text(str, x, y, color = '#fff', size = 16, align = 'left', baseline = 'top') {
    this.ctx.fillStyle = color;
    this.ctx.font = `bold ${size}px monospace`;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = baseline;
    this.ctx.fillText(str, Math.round(x), Math.round(y));
  }

  // Non-bold, no-outline text. Use for emoji icons whose thin strokes get
  // crushed into solid blobs by the bold weight + 8-direction outline pass.
  textThin(str, x, y, color = '#fff', size = 16, align = 'left', baseline = 'top') {
    this.ctx.fillStyle = color;
    this.ctx.font = `${size}px monospace`;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = baseline;
    this.ctx.fillText(str, Math.round(x), Math.round(y));
  }

  textOutlined(str, x, y, color = '#fff', outlineColor = '#000', size = 16, align = 'left', baseline = 'top') {
    this.ctx.font = `bold ${size}px monospace`;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = baseline;
    const ox = Math.round(x);
    const oy = Math.round(y);
    // Outline
    this.ctx.fillStyle = outlineColor;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue;
        this.ctx.fillText(str, ox + dx, oy + dy);
      }
    }
    // Main text
    this.ctx.fillStyle = color;
    this.ctx.fillText(str, ox, oy);
  }

  // Pixel-art lettering (pixel-text.js) centred on (x, y): the font rendered
  // on the sprite-pixel grid with banded fill, shadow and outline from
  // `style`. `glowAlpha` > 0 first lays the style's stepped glow under it.
  // Returns the drawn rect. Without a DOM (headless tests) it falls back
  // to textOutlined.
  pixelText(str, x, y, style, glowAlpha = 0) {
    const sprite = pixelTextSprite(str, style);
    if (!sprite) {
      const size = style.sizeCells * PIXEL_SCALE;
      this.textOutlined(str, x, y, (style.bands || ['#fff'])[0], style.outline || '#000', size, 'center', 'middle');
      return { x: x - this.measureText(str, size) / 2, y: y - size / 2, w: this.measureText(str, size), h: size };
    }
    const w = sprite.w * PIXEL_SCALE;
    const h = sprite.h * PIXEL_SCALE;
    const ox = Math.round(x - w / 2);
    const oy = Math.round(y - h / 2);
    this.ctx.imageSmoothingEnabled = false;
    if (glowAlpha > 0 && sprite.glow) {
      this.ctx.globalAlpha = glowAlpha;
      this.ctx.drawImage(sprite.glow, ox, oy, w, h);
      this.ctx.globalAlpha = 1;
    }
    this.ctx.drawImage(sprite.canvas, ox, oy, w, h);
    return { x: ox, y: oy, w, h };
  }

  // Outlined text that shrinks to fit within maxWidth.
  textOutlinedFit(str, x, y, maxWidth, color, outlineColor, size, align, baseline) {
    let s = size;
    while (s > 8 && this.measureText(str, s) > maxWidth) s--;
    this.textOutlined(str, x, y, color, outlineColor, s, align, baseline);
  }

  measureText(str, size = 16) {
    this.ctx.font = `bold ${size}px monospace`;
    return this.ctx.measureText(str).width;
  }

  // Shift that puts the visible ink of `str`, drawn 'center'/'middle', on
  // the given point. Emoji fonts disagree on side bearings and baselines
  // (Apple Color Emoji sat ⚔ and 💥 low-right on iPhone), so centre on the
  // glyph's actual bounding box. { dx: 0, dy: 0 } where metrics are missing.
  glyphCentreOffset(str, size = 16) {
    this.ctx.font = `bold ${size}px monospace`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    const m = this.ctx.measureText(str);
    if (!m || !Number.isFinite(m.actualBoundingBoxLeft) || !Number.isFinite(m.actualBoundingBoxAscent)) {
      return { dx: 0, dy: 0 };
    }
    return {
      dx: (m.actualBoundingBoxLeft - m.actualBoundingBoxRight) / 2,
      dy: (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2,
    };
  }

  // Draw word-wrapped text within maxWidth. Returns the total height used.
  textWrapped(str, x, y, maxWidth, color = '#fff', size = 16, align = 'left', lineHeight = 1.3) {
    this.ctx.font = `bold ${size}px monospace`;
    this.ctx.fillStyle = color;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = 'top';

    const words = str.split(' ');
    const lines = [];
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (this.ctx.measureText(test).width > maxWidth && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);

    const lh = Math.round(size * lineHeight);
    const ox = Math.round(x);
    let oy = Math.round(y);
    for (const line of lines) {
      this.ctx.fillText(line, ox, oy);
      oy += lh;
    }
    return lines.length * lh;
  }

  // Draw a simple pixel-art style sprite from a pixel map
  // pixels is an array of rows, each row is a string where each char maps to a color
  // palette is { char: color }, space = transparent
  // Draw a pixel-art sprite centred on (x, y).
  //
  // This used to emit one fillRect (plus a fillStyle switch) per non-empty
  // pixel — roughly 100 canvas calls per guard, per frame, with no culling.
  // At late-wave head counts that alone ate the frame budget. Each
  // (sprite, palette, scale) triple is now rasterised into an offscreen
  // canvas once and blitted with a single drawImage. Output is identical:
  // the offscreen bitmap is laid out with the same origin rounding.
  pixelSprite(x, y, pixels, palette, scale = 2) {
    const sprite = cachedSprite(pixels, palette, scale);
    if (sprite) {
      const ox = Math.round(x - sprite.width / 2);
      const oy = Math.round(y - sprite.height / 2);
      this.ctx.drawImage(sprite, ox, oy);
      return;
    }
    // No document (headless) or canvas creation failed — draw per pixel.
    const rows = pixels.length;
    const cols = pixels[0].length;
    const ox = Math.round(x - (cols * scale) / 2);
    const oy = Math.round(y - (rows * scale) / 2);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ch = pixels[r][c];
        if (ch === ' ' || ch === '.') continue;
        const color = palette[ch];
        if (color) {
          this.ctx.fillStyle = color;
          this.ctx.fillRect(ox + c * scale, oy + r * scale, scale, scale);
        }
      }
    }
  }

  // Health bar
  healthBar(x, y, w, h, ratio, fgColor = '#e74c3c', bgColor = '#333') {
    this.rect(x, y, w, h, bgColor);
    if (ratio > 0) {
      this.rect(x, y, w * Math.max(0, Math.min(1, ratio)), h, fgColor);
    }
  }

  // Save/restore for camera transforms
  save() { this.ctx.save(); }
  restore() { this.ctx.restore(); }

  translate(x, y) { this.ctx.translate(x, y); }
  scale(sx, sy) { this.ctx.scale(sx, sy !== undefined ? sy : sx); }
  rotate(angle) { this.ctx.rotate(angle); }

  setAlpha(a) { this.ctx.globalAlpha = a; }
  resetAlpha() { this.ctx.globalAlpha = 1; }
}
